import React, { FC, memo, useMemo } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Box,
  Button,
  LinearProgress
} from '@material-ui/core';
import { AlternatingColorTableRow } from 'src/components';
import { Pagination } from '@material-ui/lab';
import ListIcon from '@material-ui/icons/List';
import GetAppIcon from '@material-ui/icons/GetApp';
import { formatCurrency } from 'src/utils';
import useSellOut from 'src/hooks/use-sell-out';
import { GetSellOutRequest } from 'src/redux/slices/sell-out';
import { useNavigate } from 'react-router';

interface Props {}

const Component: FC<Props> = () => {
  const navigate = useNavigate();
  const {
    appliedFilter,
    sellOutList,
    isLoading,
    meta,
    getSellOutList,
    getSellOutCsv
  } = useSellOut();

  const headers = useMemo(() => {
    if (sellOutList && sellOutList.length > 0) {
      return Object.keys(sellOutList[0])?.map((key) =>
        key
          .replace(/_/g, ' ')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
    }
    return [];
  }, [sellOutList]);

  const getSellOutListings = (params: GetSellOutRequest) => {
    getSellOutList(params);
  };

  const onHandleChangePage = (filter: GetSellOutRequest) => {
    const params = new URLSearchParams();

    if (filter.keyword) params.set('keyword', filter.keyword);
    if (filter.start_date) params.set('start_date', filter.start_date);
    if (filter.end_date) params.set('end_date', filter.end_date);
    if (filter.category_ids)
      params.set('category_ids', filter.category_ids.join(','));
    params.set('page', filter.page?.toString() || '1');

    if (filter?.manufacturer_ids?.length)
      params.set('manufacturer_ids', filter.manufacturer_ids.join(','));

    if (filter?.branch_ids?.length)
      params.set('branch_ids', filter.branch_ids.join(','));

    getSellOutList(filter);
    navigate(`?${params}`);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: 2,
          marginBottom: 5
        }}
      >
        <Button
          variant="outlined"
          endIcon={<ListIcon />}
          onClick={() => getSellOutListings(appliedFilter)}
          color="primary"
        >
          Show List
        </Button>
        {sellOutList && sellOutList?.length > 0 ? (
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => getSellOutCsv(appliedFilter)}
          >
            Export CSV
          </Button>
        ) : null}
      </Box>
      {isLoading ? <LinearProgress /> : null}
      {sellOutList && sellOutList?.length > 0 ? (
        <>
          <Box
            style={{
              minWidth: 650,
              minHeight: 100,
              maxHeight: 500,
              overflowY: 'auto'
            }}
          >
            <TableContainer component={Paper}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow style={{ flex: 1, width: '100%' }}>
                    {headers?.map((header, index) => (
                      <TableCell key={index}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sellOutList?.map((row: any, index) => (
                    <AlternatingColorTableRow key={index} hover>
                      {Object.keys(row)?.map((row_key, index) => (
                        <>
                          {row_key === 'dealers_price' ||
                          row_key === 'retail_price' ? (
                            <TableCell key={index}>
                              {formatCurrency(row[row_key]) ?? 'N/A'}
                            </TableCell>
                          ) : (
                            <TableCell key={index}>
                              {row[row_key] ?? 'N/A'}
                            </TableCell>
                          )}
                        </>
                      ))}
                    </AlternatingColorTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Pagination
            count={meta?.last_page}
            page={meta?.current_page}
            onChange={(e, page) => {
              onHandleChangePage({ ...appliedFilter, page: page });
            }}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 10
            }}
          />
        </>
      ) : null}
    </Box>
  );
};

export const SellOutListings = memo(Component);
